import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../App';
import { CheckCircle2, Send, Upload, FileText, X } from 'lucide-react';

const QUESTIONS = [
  "Why do you want to work with Pure Souls specifically?",
  "Tell us about a time you helped someone through a difficult moment.",
  "What does compassion mean to you in a workplace?",
  "This role involves supporting grieving families and caring for pets after they have passed. How do you feel about that responsibility?",
  "Tell us about a time you made a mistake at work. What happened, and what did you do next?",
  "Tell us about a time you noticed something small that others missed.",
  "What does reliability mean to you?",
  "How do you respond when someone gives you constructive feedback?",
  "What kind of workplace brings out your best?",
  "Why should we trust you with work that requires dignity, care, privacy, and attention to detail?",
];

const LOGO = "https://customer-assets.emergentagent.com/job_staff-ops-portal/artifacts/jqjh0r3w_Logo%20Pure%20Souls%20%20svg.svg";

export default function PublicApplyPage() {
  const [vacancies, setVacancies] = useState([]);
  const [form, setForm] = useState({ vacancy_id: '', candidate_name: '', phone: '', email: '', source: 'Pure Souls Website' });
  const [answers, setAnswers] = useState({});
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeErr, setResumeErr] = useState('');
  const [locked, setLocked] = useState(false);
  const [closed, setClosed] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [candidateNum, setCandidateNum] = useState('');

  useEffect(() => {
    const vacancyParam = new URLSearchParams(window.location.search).get('vacancy');
    fetch(`${BACKEND_URL}/api/recruitment/vacancies/public/open`)
      .then(r => r.json())
      .then(d => {
        const list = d.vacancies || [];
        setVacancies(list);
        if (vacancyParam) {
          const match = list.find(v => v.id === vacancyParam);
          if (match) { setForm(f => ({ ...f, vacancy_id: match.id })); setLocked(true); }
          else { setClosed(true); }
        } else if (list.length) {
          setForm(f => ({ ...f, vacancy_id: list[0].id }));
        }
      })
      .catch(() => {})
      .finally(() => setLoadingRoles(false));
  }, []);

  const onFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (!['pdf', 'doc', 'docx'].includes(ext)) { setResumeErr('Please upload a PDF, DOC or DOCX file.'); setResumeFile(null); return; }
    if (f.size > 10 * 1024 * 1024) { setResumeErr('That file is larger than 10MB. Please upload a smaller file.'); setResumeFile(null); return; }
    setResumeErr('');
    setResumeFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.candidate_name.trim()) { setError('Please enter your name'); return; }
    if (!form.email.trim()) { setError('Please enter your email'); return; }
    if (!resumeFile) { setError('Please attach your resume (PDF, DOC or DOCX).'); return; }
    setSubmitting(true);
    try {
      // 1) upload résumé
      const fd = new FormData();
      fd.append('file', resumeFile);
      const upRes = await fetch(`${BACKEND_URL}/api/recruitment/apply/resume`, { method: 'POST', body: fd });
      if (!upRes.ok) {
        const j = await upRes.json().catch(() => ({}));
        throw new Error(j.detail || 'resume');
      }
      const up = await upRes.json();
      // 2) submit application
      const body = { ...form, resume_file_url: up.storage_path, resume_filename: up.filename };
      QUESTIONS.forEach((q, i) => { body[`q${i + 1}`] = answers[i] || ''; });
      const res = await fetch(`${BACKEND_URL}/api/recruitment/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setCandidateNum(data.candidate_number);
      setSubmitted(true);
    } catch (err) {
      setError(err.message && err.message !== 'resume' ? err.message : 'Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="apply-page">
        <div className="apply-card apply-success">
          <div className="apply-logo-wrap"><img src={LOGO} alt="Pure Souls" className="apply-logo" /></div>
          <CheckCircle2 size={48} className="apply-success-icon" />
          <h1 className="apply-success-title">Application Submitted</h1>
          <p className="apply-success-text">Thank you for your application, <strong>{form.candidate_name}</strong>. Your reference number is <strong>{candidateNum}</strong>.</p>
          <p className="apply-success-text">We've sent a confirmation to <strong>{form.email}</strong>. Our team will review your application and be in touch.</p>
        </div>
      </div>
    );
  }

  // Role-specific link pointing at a role that is no longer open
  if (closed) {
    return (
      <div className="apply-page">
        <div className="apply-card apply-success">
          <div className="apply-logo-wrap"><img src={LOGO} alt="Pure Souls" className="apply-logo" /></div>
          <h1 className="apply-success-title">Applications Closed</h1>
          <p className="apply-success-text">Thank you for your interest. This position is no longer accepting applications.</p>
          <p className="apply-success-text">Please check back soon or reach out to us at <strong>info@puresouls.com.au</strong>.</p>
        </div>
      </div>
    );
  }

  if (!loadingRoles && vacancies.length === 0 && !closed) {
    return (
      <div className="apply-page">
        <div className="apply-card apply-success">
          <div className="apply-logo-wrap"><img src={LOGO} alt="Pure Souls" className="apply-logo" /></div>
          <h1 className="apply-success-title">No Open Positions</h1>
          <p className="apply-success-text">We don't have any open roles right now. Please check back soon.</p>
        </div>
      </div>
    );
  }

  const selectedVacancy = vacancies.find(v => v.id === form.vacancy_id);

  return (
    <div className="apply-page">
      <div className="apply-card">
        <div className="apply-logo-wrap"><img src={LOGO} alt="Pure Souls" className="apply-logo" /></div>
        <h1 className="apply-title">Join Our Team</h1>
        <p className="apply-subtitle">Pure Souls Pet Aquacremation — Townsville, QLD</p>

        {/* Role selector when not locked to a specific link and there are multiple roles */}
        {!locked && vacancies.length > 1 && (
          <div className="apply-field" style={{ marginBottom: 12 }}>
            <label>Which role are you applying for?</label>
            <select value={form.vacancy_id} onChange={e => setForm(f => ({ ...f, vacancy_id: e.target.value }))} data-testid="apply-role-select">
              {vacancies.map(v => <option key={v.id} value={v.id}>{v.role_title}</option>)}
            </select>
          </div>
        )}

        {selectedVacancy && (
          <div className="apply-role-card" data-testid="apply-role-card">
            <h2 className="apply-role-title">{selectedVacancy.role_title}</h2>
            <p className="apply-role-meta">{selectedVacancy.employment_type} · {selectedVacancy.hours} · {selectedVacancy.location}</p>
            {selectedVacancy.pathway && <p className="apply-role-pathway">{selectedVacancy.pathway}</p>}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {error && <div className="apply-error" data-testid="apply-error">{error}</div>}

          <div className="apply-section">
            <h3 className="apply-section-title">Your Details</h3>
            <div className="apply-field">
              <label>Full Name *</label>
              <input type="text" value={form.candidate_name} onChange={e => setForm(f => ({ ...f, candidate_name: e.target.value }))} required placeholder="Your full name" data-testid="apply-name" />
            </div>
            <div className="apply-field-row">
              <div className="apply-field">
                <label>Phone</label>
                <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="04xx xxx xxx" data-testid="apply-phone" />
              </div>
              <div className="apply-field">
                <label>Email *</label>
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required placeholder="your@email.com" data-testid="apply-email" />
              </div>
            </div>
            <div className="apply-field">
              <label>How did you hear about us?</label>
              <select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} data-testid="apply-source">
                {['Pure Souls Website', 'Facebook', 'Local Facebook Group', 'Indeed', 'Jora', 'Seek', 'Veterinary Clinic Referral', 'TAFE Queensland', 'Word of Mouth', 'LinkedIn', 'Other'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Résumé upload (required) */}
          <div className="apply-section">
            <h3 className="apply-section-title">Your Resume *</h3>
            <p className="apply-section-desc">Please attach your resume — PDF, DOC or DOCX, up to 10MB.</p>
            {!resumeFile ? (
              <label className="apply-upload" data-testid="apply-resume-label">
                <Upload size={20} />
                <span>Click to choose your resume</span>
                <input type="file" accept=".pdf,.doc,.docx" onChange={onFile} style={{ display: 'none' }} data-testid="apply-resume-input" />
              </label>
            ) : (
              <div className="apply-file-chip" data-testid="apply-resume-chip">
                <FileText size={18} />
                <span className="apply-file-name">{resumeFile.name}</span>
                <button type="button" onClick={() => setResumeFile(null)} aria-label="Remove file" data-testid="apply-resume-remove"><X size={16} /></button>
              </div>
            )}
            {resumeErr && <div className="apply-error" style={{ marginTop: 8 }}>{resumeErr}</div>}
          </div>

          <div className="apply-section">
            <h3 className="apply-section-title">Application Questions</h3>
            <p className="apply-section-desc">Please take your time with these questions. There are no right or wrong answers — we're interested in who you are and how you think.</p>
            {QUESTIONS.map((q, i) => (
              <div key={i} className="apply-question">
                <label><span className="apply-q-num">{i + 1}</span> {q}</label>
                <textarea value={answers[i] || ''} onChange={e => setAnswers(a => ({ ...a, [i]: e.target.value }))} rows={3} placeholder="Your answer..." data-testid={`apply-q${i + 1}`} />
              </div>
            ))}
          </div>

          <button type="submit" disabled={submitting} className="apply-submit" data-testid="apply-submit">
            <Send size={18} />
            {submitting ? 'Submitting...' : 'Submit Application'}
          </button>
        </form>
      </div>
    </div>
  );
}
